import { FC, ReactNode } from 'react';

import { IRoutingConfigVersion } from '@entities/routing/config-version';
import ArrowCircleDownOutlinedIcon from '@mui/icons-material/ArrowCircleDownOutlined';
import ArrowCircleUpOutlinedIcon from '@mui/icons-material/ArrowCircleUpOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { PUBLISH_COPY, getPublishMode } from './publishMode';
import { ActorLabel } from '../../components/ActorLabel';

interface IProps {
  // The version that goes live: a draft (publish) or an archived version (activate) —
  // the wording follows its status.
  version: IRoutingConfigVersion;
  // null when there is no active version yet or it could not be loaded.
  active: IRoutingConfigVersion | null;
  isActiveUnavailable: boolean;
  isReloading: boolean;
  // The active version when it is not the base of the draft (FE-side conflict check).
  conflict: IRoutingConfigVersion | null;
  // The version carries the same policies and rules as the active one.
  isUnchanged: boolean;
  onReload: () => void;
}

interface IVersionRefProps {
  version: IRoutingConfigVersion;
}

// "v58 “name”" — long names ellipsize, the full name sits in the tooltip.
const VersionRef: FC<IVersionRefProps> = ({ version }) => (
  <span className='routing-publish-swap__version'>
    <span className='routing-version-number'>v{version.id}</span>{' '}
    <span className='routing-publish-swap__name' title={version.name}>
      “{version.name}”
    </span>
  </span>
);

const describeSize = (version: IRoutingConfigVersion) => {
  const policies = version.policies?.length ?? 0;
  const rules = (version.policies ?? []).reduce((sum, policy) => sum + (policy.rules?.length ?? 0), 0);
  const policyLabel = policies === 1 ? '1 policy' : `${policies} policies`;
  const ruleLabel = rules === 1 ? '1 rule' : `${rules} rules`;

  return `${policyLabel} · ${ruleLabel}`;
};

type TSwapTone = 'live' | 'archive' | 'none' | 'warning';

interface ISwapRowProps {
  tone: TSwapTone;
  icon: ReactNode;
  label: string;
  version?: IRoutingConfigVersion;
  hint: string;
  action?: ReactNode;
  role?: 'status';
}

// One side of the swap: icon + coloured keyword, the version, and a one-line hint.
const SwapRow: FC<ISwapRowProps> = ({ tone, icon, label, version, hint, action, role }) => (
  <li className={`routing-publish-swap__row routing-publish-swap__row--${tone}`} role={role}>
    <span className='routing-publish-swap__icon' aria-hidden='true'>
      {icon}
    </span>
    <div className='routing-publish-swap__body'>
      <div className='routing-publish-swap__label'>{label}</div>
      {version && <VersionRef version={version} />}
      <div className='routing-publish-swap__hint'>
        {version ? `${describeSize(version)} — ${hint}` : hint}
      </div>
      {action}
    </div>
  </li>
);

// What the switch swaps, at a glance, plus the warning when the version active
// now is no longer the one this draft was branched from.
const PublishSummary: FC<IProps> = ({
  version,
  active,
  isActiveUnavailable,
  isReloading,
  conflict,
  isUnchanged,
  onReload,
}) => {
  const copy = PUBLISH_COPY[getPublishMode(version)];
  const reloadButton = (
    <button
      type='button'
      className='routing-version-switcher-btn'
      onClick={onReload}
      disabled={isReloading}
    >
      {isReloading ? 'Reloading…' : 'Reload'}
    </button>
  );

  // A root draft was never branched, so the conflict copy cannot name a base.
  const isRootDraft = version.parent_version_id === null;

  const renderArchiveRow = () => {
    if (active) {
      return (
        <SwapRow
          tone='archive'
          icon={<ArrowCircleDownOutlinedIcon />}
          label='Goes to the archive'
          version={active}
          hint='kept and can be restored later.'
        />
      );
    }
    if (isActiveUnavailable) {
      return (
        <SwapRow
          tone='warning'
          role='status'
          icon={<HelpOutlineIcon />}
          label='Unknown'
          hint='The active version could not be loaded, so it is unknown which version goes to the archive.'
          action={reloadButton}
        />
      );
    }
    return (
      <SwapRow
        tone='none'
        icon={<InfoOutlinedIcon />}
        label='Nothing to archive'
        hint='There is no active version yet. This will be the first live one.'
      />
    );
  };

  return (
    <div className='routing-publish-modal__summary'>
      <div className='routing-publish-modal__section-label'>What happens</div>
      <ul className='routing-publish-swap'>
        <SwapRow
          tone='live'
          icon={<ArrowCircleUpOutlinedIcon />}
          label='Goes live'
          version={version}
          hint={copy.liveHint}
        />
        {renderArchiveRow()}
      </ul>

      {isUnchanged && active && (
        <p className='routing-publish-modal__note'>
          {copy.subject} has the same policies and rules as v{active.id} — {copy.verb} changes
          nothing in routing.
        </p>
      )}

      {conflict && (
        <div className='routing-publish-modal__conflict' role='status'>
          <p className='routing-publish-modal__conflict-title'>
            {isRootDraft
              ? 'This draft has no base version.'
              : 'The active version changed since this draft was created.'}
          </p>
          <p>
            {isRootDraft ? (
              <>It was not branched from any version, but </>
            ) : (
              <>This draft is based on v{version.parent_version_id}, but </>
            )}
            <VersionRef version={conflict} /> is active now, published by{' '}
            <ActorLabel actor={conflict.updated_by} />
            {conflict.published_at && ` on ${new Date(conflict.published_at).toLocaleString()}`}.
            {/* With identical policies there is nothing to lose, and the note above says so. */}
            {!isUnchanged && ' Publishing archives it without carrying over its changes.'}
          </p>
          {reloadButton}
        </div>
      )}
    </div>
  );
};

export default PublishSummary;
